/**
 * User and institution administration (`app/services/admin/user_admin_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { EmptyObject, PartialKey } from '@brainfocus/web/utils/types';
import { assertPojo } from '@brainfocus/web/api/response';
import type { ListManagedPayload } from '@brainfocus/web/api/types';

/**
 * Create new or update existing institution
 */
export const saveInstitution = client.endpointJson<SaveInstitutionPayload, EmptyObject>('/admin/save-institution');

/**
 * As a admin, get list of all existing institutions
 */
export const getInstitutions = client.endpointJson<ListManagedPayload, { institutions: ManagedInstitution[] }>('/admin/get-institutions');

export async function deleteUser(id: number): Promise<EmptyObject> {
    await client.request('POST', `/admin/delete-user/${id}`, {});
    return {};
}

export async function deleteInstitution(id: number): Promise<EmptyObject> {
    await client.request('POST', `/admin/delete-institution/${id}`, {});
    return {};
}

export async function getUsers(payload: ListManagedPayload): Promise<ManagedUser[]> {
    const response = await client.request('POST', '/admin/get-users', {
        offset: payload.offset,
        limit: payload.limit,
    });

    assertPojo(response);
    const users = (response as any).users.map((a: any) => ({
        id: a.id,
        firstName: a.first_name,
        lastName: a.last_name,
        email: a.email,
        institution: a.institution,
        institutionId: a.institution_id,
        role: a.role,
        tocVersion: a.toc_version,
        privacyVersion: a.privacy_version,
        tocAcceptedServerAt: a.toc_accepted_server_at,
        signupAt: a.signup_at,
        lastLoginAt: a.last_login_at,
    }));

    return users;
}

export async function getInstitutionMap(): Promise<Record<number, string>> {
    const response = await client.request('POST', '/admin/get-institution-map', {});
    assertPojo<{ map: Record<number, string> }>(response);
    return response.map;
}

export async function saveUser(p: SaveUserPayload): Promise<{ newPassword?: string }> {
    const response = await client.request('POST', '/admin/save-user', {
        id: p.id,
        first_name: p.firstName,
        last_name: p.lastName,
        email: p.email,
        institution_id: p.institutionId,
        role: p.role,
    });
    assertPojo<{ new_user_password: string }>(response);
    return { newPassword: response.new_user_password };
}

export async function updatePassword(id: number, pwd: string): Promise<{ newPassword: string }> {
    const response = await client.request('POST', `/admin/update-password/${id}`, {
        password: pwd,
    });
    assertPojo<{ new_user_password: string }>(response);
    return { newPassword: response.new_user_password };
}

export interface ManagedUser {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    institution: string;
    institutionId: number;
    role: UserRole;
    tocVersion: string;
    privacyVersion: string;
    tocAcceptedServerAt: string | null;
    /** Account creation time (signup time) — trial follow-up (#587). */
    signupAt: string | null;
    /** Most recent successful login, or null if the user never logged in (#587). */
    lastLoginAt: string | null;
}

export interface SaveUserPayload {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    institutionId: number;
    role: UserRole;
}

export enum UserRole {
    User = 'user',
    InstitutionAdmin = 'institution_admin',
    SuperAdmin = 'super_admin',
}

export interface ManagedInstitution {
    id: number;
    name: string;
    credits: number | null;
    isTrial: boolean;
}

export type SaveInstitutionPayload = PartialKey<ManagedInstitution, 'id'>;
